/**
 * PATH: packages/email-hub/src/feature/components/whoCanSee.ts
 * FILE_ID: EMAIL-WHOCANSEE-TYPES-001
 * PURPOSE: Wire types + pure helpers for the "who can see this email" surface.
 *          Kept separate from the components so fast-refresh stays clean and the
 *          helpers are independently unit-testable (I→O).
 * RELATED:
 *   - WhoCanSeeFlow.tsx / WhoCanSeeButton.tsx: consumers
 *   - organisation/services/email_who_can_see.py: the matching backend contract
 */

export interface WhoCanSeeMember {
  id: number
  name: string
  email: string
  role: string
  position: string
  is_primary: boolean
}

export interface WhoCanSeeChannel {
  type: 'OWNER' | 'SHARED_MAILBOX' | 'BOARD' | 'EXPLICIT' | 'ORG_ADMIN' | string
  label: string
  board_id?: number
  board_name?: string
  members: WhoCanSeeMember[]
}

export interface WhoCanSeeData {
  email_id: string
  primary: WhoCanSeeMember | null
  channels: WhoCanSeeChannel[]
  total: number
}

/** Deterministic avatar tint from a stable key (pure — testable). */
export function avatarTint(key: string): string {
  const palette = [
    'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
    'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
    'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300',
  ]
  let h = 0
  for (let i = 0; i < key.length; i += 1) h = (h * 31 + key.charCodeAt(i)) >>> 0
  return palette[h % palette.length]
}

/** First+last initials of a display name (pure — testable). */
export function memberInitials(name: string): string {
  const parts = (name || '?').trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}
